import React, { useState, useRef, useEffect } from "react";
import AdminWrapper from "../../../components/AdminWrapper";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { ValidateUser } from "../../../redux/reducer/ValidateUser";
import {
  UpdateAdminProfile,
  ChangeAdminPassword,
  resetProfileState,
} from "../../../redux/reducer/UpdateAdminProfile";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Switch,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  Skeleton,
  InputAdornment,
  Badge,
} from "@mui/material";
import {
  Save,
  Security,
  Notifications,
  Email,
  Payment,
  Person,
  Visibility,
  VisibilityOff,
  AdminPanelSettings,
  CameraAlt,
  Lock,
} from "@mui/icons-material";
import { ROLE_LABELS } from "../../../config/adminRoles";

const SettingSection = ({ title, icon, children }) => (
  <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
      <Avatar sx={{ bgcolor: "#e9456015", width: 40, height: 40 }}>
        {React.cloneElement(icon, { sx: { color: "#e94560", fontSize: 20 } })}
      </Avatar>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
    </Box>
    {children}
  </Paper>
);

const AdminSettings = () => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const open = useSelector((state) => state.sidebar.open);
  const { validateUser } = useSelector((state) => state);
  const user = validateUser?.UserValidate?.data?.user;
  const adminAuth = useSelector((state) => state.adminAuth);
  const admin = adminAuth?.admin || user;

  const { loading, success, error, passwordLoading, passwordSuccess, passwordError } = useSelector(
    (state) => state.updateAdminProfile
  );

  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(admin?.profileImage || null);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [profileData, setProfileData] = useState({
    name: admin?.name || "",
    email: admin?.email || "",
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [settings, setSettings] = useState({
    emailNotifications: true,
    newUserNotifications: true,
    propertyApprovalNotifications: true,
    platformFee: "5",
  });

  // Update state when admin data changes
  useEffect(() => {
    if (admin) {
      setProfileData({
        name: admin.name || "",
        email: admin.email || "",
      });
      setImagePreview(admin.profileImage || null);
    }
  }, [admin]);

  // Handle success/error messages
  useEffect(() => {
    if (success) {
      toast.success("Profile updated successfully!");
      dispatch(ValidateUser()); // Refresh user data
      dispatch(resetProfileState());
    }
    if (error) {
      toast.error(error);
      dispatch(resetProfileState());
    }
    if (passwordSuccess) {
      toast.success("Password changed successfully!");
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
      dispatch(resetProfileState());
    }
    if (passwordError) {
      toast.error(passwordError);
      dispatch(resetProfileState());
    }
  }, [success, error, passwordSuccess, passwordError, dispatch]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleProfileChange = (field) => (e) => {
    setProfileData({ ...profileData, [field]: e.target.value });
  };

  const handlePasswordChange = (field) => (e) => {
    setPasswordData({ ...passwordData, [field]: e.target.value });
  };

  const handleSettingsChange = (field) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setSettings({ ...settings, [field]: value });
  };

  const handleSaveProfile = () => {
    if (!profileData.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!profileData.email.trim()) {
      toast.error("Email is required");
      return;
    }

    const formData = new FormData();
    formData.append("name", profileData.name);
    formData.append("email", profileData.email);
    if (profileImage) {
      formData.append("profileImage", profileImage);
    }

    dispatch(UpdateAdminProfile(formData));
  };

  const handleChangePassword = () => {
    if (!passwordData.currentPassword) {
      toast.error("Current password is required");
      return;
    }
    if (!passwordData.newPassword) {
      toast.error("New password is required");
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    dispatch(
      ChangeAdminPassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      })
    );
  };

  // Loading Skeleton
  const LoadingSkeleton = () => (
    <>
      <Box sx={{ mb: 3 }}>
        <Skeleton variant="text" width={200} height={40} />
        <Skeleton variant="text" width={280} height={24} />
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          {[1, 2, 3].map((i) => (
            <Paper key={i} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                <Skeleton variant="circular" width={40} height={40} />
                <Skeleton variant="text" width={150} height={30} />
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Skeleton variant="rounded" height={56} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Skeleton variant="rounded" height={56} />
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Skeleton variant="circular" width={120} height={120} sx={{ mx: "auto", mb: 2 }} />
              <Skeleton variant="text" width={150} height={30} sx={{ mx: "auto" }} />
              <Skeleton variant="text" width={180} height={20} sx={{ mx: "auto" }} />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </>
  );

  const pageLoading = !admin && !user;

  return (
    <>
      <AdminWrapper />
      <Box
        sx={{
          ml: { xs: 0, md: open ? "260px" : "65px" },
          transition: "margin 0.3s ease",
          minHeight: "100vh",
          bgcolor: "#f8fafc",
          p: { xs: 2, md: 3 },
          pt: { xs: 10, md: 11 },
        }}
      >
        {pageLoading ? (
          <LoadingSkeleton />
        ) : (
          <>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#1e293b" }}>
                My Profile
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage your profile, security and notification settings
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {/* Left Column */}
              <Grid item xs={12} md={8}>
                {/* Profile Picture & Basic Info */}
                <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                    <Avatar sx={{ bgcolor: "#e9456015", width: 40, height: 40 }}>
                      <Person sx={{ color: "#e94560", fontSize: 20 }} />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Profile Information
                    </Typography>
                  </Box>

                  <Grid container spacing={3}>
                    {/* Profile Picture */}
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ textAlign: "center" }}>
                        <Badge
                          overlap="circular"
                          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                          badgeContent={
                            <IconButton
                              onClick={() => fileInputRef.current?.click()}
                              sx={{
                                bgcolor: "#e94560",
                                color: "white",
                                width: 36,
                                height: 36,
                                "&:hover": { bgcolor: "#d13652" },
                              }}
                            >
                              <CameraAlt sx={{ fontSize: 18 }} />
                            </IconButton>
                          }
                        >
                          <Avatar
                            src={imagePreview}
                            sx={{
                              width: 120,
                              height: 120,
                              bgcolor: "#e94560",
                              fontSize: 48,
                              border: "4px solid #fff",
                              boxShadow: "0 4px 14px rgba(0,0,0,0.1)",
                            }}
                          >
                            {(admin?.name || "A").charAt(0).toUpperCase()}
                          </Avatar>
                        </Badge>
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleImageChange}
                          accept="image/*"
                          style={{ display: "none" }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                          Click camera to change photo
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Max size: 5MB
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Name & Email */}
                    <Grid item xs={12} sm={8}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Full Name"
                            value={profileData.name}
                            onChange={handleProfileChange("name")}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Person sx={{ color: "#9ca3af" }} />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Email Address"
                            value={profileData.email}
                            onChange={handleProfileChange("email")}
                            type="email"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Email sx={{ color: "#9ca3af" }} />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            variant="contained"
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                            onClick={handleSaveProfile}
                            disabled={loading}
                            sx={{
                              bgcolor: "#e94560",
                              "&:hover": { bgcolor: "#d13652" },
                            }}
                          >
                            {loading ? "Saving..." : "Save Profile"}
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Security Settings - Change Password */}
                <SettingSection title="Change Password" icon={<Security />}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Current Password"
                        type={showCurrentPassword ? "text" : "password"}
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange("currentPassword")}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock sx={{ color: "#9ca3af" }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                edge="end"
                              >
                                {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="New Password"
                        type={showNewPassword ? "text" : "password"}
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange("newPassword")}
                        helperText="Minimum 6 characters"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock sx={{ color: "#9ca3af" }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowNewPassword(!showNewPassword)}
                                edge="end"
                              >
                                {showNewPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Confirm New Password"
                        type={showConfirmPassword ? "text" : "password"}
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange("confirmPassword")}
                        error={
                          passwordData.confirmPassword &&
                          passwordData.newPassword !== passwordData.confirmPassword
                        }
                        helperText={
                          passwordData.confirmPassword &&
                          passwordData.newPassword !== passwordData.confirmPassword
                            ? "Passwords do not match"
                            : " "
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock sx={{ color: "#9ca3af" }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                edge="end"
                              >
                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        startIcon={
                          passwordLoading ? <CircularProgress size={20} color="inherit" /> : <Security />
                        }
                        onClick={handleChangePassword}
                        disabled={passwordLoading}
                        sx={{
                          bgcolor: "#6366f1",
                          "&:hover": { bgcolor: "#4f46e5" },
                        }}
                      >
                        {passwordLoading ? "Changing..." : "Change Password"}
                      </Button>
                    </Grid>
                  </Grid>
                </SettingSection>

                {/* Notification Settings */}
                <SettingSection title="Notification Settings" icon={<Notifications />}>
                  <List disablePadding>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary="Email Notifications"
                        secondary="Receive email notifications for important updates"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.emailNotifications}
                          onChange={handleSettingsChange("emailNotifications")}
                          color="primary"
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider />
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary="New User Registrations"
                        secondary="Get notified when new owners register"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.newUserNotifications}
                          onChange={handleSettingsChange("newUserNotifications")}
                          color="primary"
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider />
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary="Property Approval Alerts"
                        secondary="Get notified when properties need approval"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.propertyApprovalNotifications}
                          onChange={handleSettingsChange("propertyApprovalNotifications")}
                          color="primary"
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                </SettingSection>

                {/* Platform Settings */}
                <SettingSection title="Platform Settings" icon={<Payment />}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Platform Fee (%)"
                        value={settings.platformFee}
                        onChange={handleSettingsChange("platformFee")}
                        type="number"
                        helperText="Commission percentage on transactions"
                      />
                    </Grid>
                  </Grid>
                </SettingSection>
              </Grid>

              {/* Right Column - Admin Info Card */}
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3, borderRadius: 3, position: "sticky", top: 100 }}>
                  <Box sx={{ textAlign: "center", mb: 3 }}>
                    <Avatar
                      src={imagePreview}
                      sx={{
                        width: 100,
                        height: 100,
                        mx: "auto",
                        mb: 2,
                        bgcolor: "#e94560",
                        fontSize: 40,
                        border: "3px solid #e9456030",
                      }}
                    >
                      {(admin?.name || "A").charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {admin?.name || "Administrator"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {admin?.email || "admin@ots.com"}
                    </Typography>
                    <Box sx={{ mt: 1.5 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          bgcolor: "#e9456015",
                          color: "#e94560",
                          px: 2,
                          py: 0.5,
                          borderRadius: 10,
                          fontWeight: 600,
                        }}
                      >
                        {ROLE_LABELS[admin?.adminRole] || "Admin"}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  <List dense>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <AdminPanelSettings sx={{ color: "#6366f1", fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Role"
                        secondary={
                          admin?.adminRole === "super_admin"
                            ? "Super Administrator"
                            : ROLE_LABELS[admin?.adminRole] || "Administrator"
                        }
                        primaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
                        secondaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Email sx={{ color: "#6366f1", fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Email"
                        secondary={admin?.email || "admin@ots.com"}
                        primaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
                        secondaryTypographyProps={{
                          variant: "body2",
                          fontWeight: 500,
                          sx: { wordBreak: "break-all" },
                        }}
                      />
                    </ListItem>
                  </List>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ p: 2, bgcolor: "#f0fdf4", borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Last Login
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: "#166534" }}>
                      {new Date().toLocaleDateString("en-US", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </>
  );
};

export default AdminSettings;
